// Signage → Feeds RSS (/signage/feeds). Fuentes de noticias que el worker
// (notificador) descarga periódicamente y guarda como titulares; la escena de
// feed 'noticias' los muestra. Aquí se administran las fuentes y se ve el estado.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Rss, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigUpdate } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Fuente {
  id: string; nombre: string; url: string; max_items: number; intervalo_min: number;
  filtro_palabras: string | null;
  activo: boolean; ultima_sync: string | null; ultimo_error: string | null;
}
interface Item { titulo: string; publicado: string | null }

export function FeedsFuentes() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Fuente> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [verId, setVerId] = useState<string | null>(null);

  const { data: fuentes = [] } = useQuery<Fuente[]>({ queryKey: ['sig-feed-fuentes-full'], queryFn: () => sigList<Fuente>('feed_fuente?select=*&deleted_at=is.null&order=nombre'), staleTime: 0 });
  const { data: items = [] } = useQuery<Item[]>({ queryKey: ['sig-feed-items', verId], enabled: !!verId, staleTime: 0, queryFn: () => sigList<Item>(`feed_item?select=titulo,publicado&fuente_id=eq.${verId}&order=orden`) });
  const invalidar = () => { qc.invalidateQueries({ queryKey: ['sig-feed-fuentes-full'] }); qc.invalidateQueries({ queryKey: ['sig-feed-fuentes'] }); };

  const guardar = async () => {
    if (!edit?.nombre || !edit.url) { toast.error('Nombre y URL son obligatorios'); return; }
    setGuardando(true);
    try {
      const body = { nombre: edit.nombre, url: edit.url, max_items: edit.max_items ?? 6, intervalo_min: edit.intervalo_min ?? 30, filtro_palabras: edit.filtro_palabras?.trim() || null, activo: edit.activo ?? true };
      if (edit.id) await sigUpdate('feed_fuente', edit.id, body); else await sigInsert('feed_fuente', body);
      setEdit(null); invalidar(); toast.success('Fuente guardada — el worker la sincroniza en el próximo ciclo');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };
  const eliminar = (f: Fuente) => sigUpdate('feed_fuente', f.id, { deleted_at: new Date().toISOString(), activo: false }).then(invalidar).then(() => toast.success('Eliminada')).catch((e) => toast.error(getErrorMessage(e)));

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Rss className="w-5 h-5" /> Feeds RSS</h1>
        <Button className="ml-auto" onClick={() => setEdit({ max_items: 6, intervalo_min: 30, activo: true })}><Plus className="w-4 h-4 mr-1" /> Nueva fuente</Button>
      </div>
      <p className="text-sm text-muted-foreground">El worker (notificador) descarga cada fuente activa según su intervalo y guarda los titulares. Agrega una escena <strong>Feed → Noticias</strong> en una plantilla y elige la fuente. Requiere el worker corriendo.</p>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Fuente</th>
            <th className="py-2 px-3 text-left">URL</th><th className="py-2 px-3 text-center">Ítems</th>
            <th className="py-2 px-3 text-center">Cada</th><th className="py-2 px-3 text-left">Última sync</th>
            <th className="py-2 px-3 text-center">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {fuentes.map((f, i) => (
              <tr key={f.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3 font-medium">{f.nombre}{f.ultimo_error && <div className="text-[11px] text-red-500" title={f.ultimo_error}>⚠ {f.ultimo_error.slice(0, 40)}</div>}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs max-w-[16rem] truncate" title={f.url}>{f.url}</td>
                <td className="py-2 px-3 text-center tabular-nums">{f.max_items}</td>
                <td className="py-2 px-3 text-center tabular-nums">{f.intervalo_min}m</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{f.ultima_sync ? new Date(f.ultima_sync).toLocaleString(LOCALE) : '—'}</td>
                <td className="py-2 px-3 text-center">{f.activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Ver titulares" onClick={() => setVerId(f.id)}>👁</button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(f)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => void eliminar(f)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!fuentes.length && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin fuentes. Agrega una URL RSS/Atom.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? 'Editar fuente' : 'Nueva fuente RSS'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
            <Campo label="URL del feed (RSS o Atom)"><input className={fieldCls} value={edit.url ?? ''} onChange={(e) => setEdit({ ...edit, url: e.target.value })} placeholder="https://…/rss" /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Máx. titulares"><input className={fieldCls} type="number" min={1} max={20} value={edit.max_items ?? 6} onChange={(e) => setEdit({ ...edit, max_items: Number(e.target.value) })} /></Campo>
              <Campo label="Refrescar cada (min)"><input className={fieldCls} type="number" min={5} max={1440} value={edit.intervalo_min ?? 30} onChange={(e) => setEdit({ ...edit, intervalo_min: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Filtrar por palabras clave (opcional, separadas por coma)">
              <input className={fieldCls} placeholder="prevención, nutrición, ejercicio" value={edit.filtro_palabras ?? ''} onChange={(e) => setEdit({ ...edit, filtro_palabras: e.target.value })} />
            </Campo>
            <p className="text-xs text-muted-foreground -mt-2">Solo pasan los titulares que contengan ALGUNA de las palabras (sin distinguir mayúsculas ni tildes).</p>
            <Switch checked={edit.activo ?? true} onChange={(v) => setEdit({ ...edit, activo: v })} label="Fuente activa" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {verId && (
        <Modal title="Titulares" onClose={() => setVerId(null)}>
          <div className="space-y-2">
            {items.length ? <ul className="space-y-2 text-sm">{items.map((it, i) => <li key={i} className="border-b pb-1.5 last:border-0">{it.titulo}{it.publicado && <span className="text-xs text-muted-foreground ml-2">{it.publicado}</span>}</li>)}</ul>
              : <div className="text-sm text-muted-foreground py-6 text-center">Sin titulares aún (el worker aún no sincroniza esta fuente).</div>}
            <div className="flex justify-end pt-2"><Button variant="ghost" onClick={() => setVerId(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
